import {Button, Dialog, SegmentedControl} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {COLORS} from "../../../constants/constants";

export const SelectedInfo = styled.div`
  position: absolute;
  bottom: 12px;
  left: 16px;
  font-size: 14px;
  display: flex;
  gap: 8px;
  align-items: center;
  height: 24px;
  line-height: 24px;
  z-index: 1;
`;

export const DangerButton = styled(Button)`
  border: none;
  color: ${COLORS.TEXT_RED};
  border-radius: 8px;
  padding: 4px 8px;
  min-height: 0;
  height: 24px;
`;

export const PanelContainer = styled.div`
  font-family: "Roboto", sans-serif;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

export const ButtonBar = styled.div<{hasWarningMessage: boolean}>`
  margin-bottom: 8px;
  height: ${(props) => (props.hasWarningMessage ? "86px" : "fit-content")};
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => (props.hasWarningMessage ? "space-between" : "center")};
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const LeftControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

export const RightControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export const FlexBox = styled.div<{gap?: [number, number]}>`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  row-gap: ${(props) => props.gap?.[0] ?? 0}px;
  column-gap: ${(props) => props.gap?.[1] ?? 0}px;
`;

export const Row = styled.div<{direction?: "row" | "column"; gap?: number; span?: number | "fit"}>`
  display: flex;
  flex-direction: ${(props) => props.direction || "row"};
  align-items: center;
  gap: ${(props) => props.gap || 0}px;
  width: ${(props) => (props.span ? (props.span === "fit" ? "fit-content" : `${(props.span / 12) * 100}%`) : "100%")};
`;

export const Col = styled.div<{direction?: "row" | "column"; gap?: number; span?: number | "fit"}>`
  display: flex;
  flex-direction: ${(props) => props.direction || "column"};
  gap: ${(props) => props.gap || 0}px;
  width: ${(props) => (props.span ? (props.span === "fit" ? "fit-content" : `${(props.span / 12) * 100}%`) : "100%")};
`;

export const GridRow = styled.div<{gridTemplateColumns?: string}>`
  display: grid;
  grid-template-columns: ${(props) => props.gridTemplateColumns || "repeat(12, 1fr)"};
  align-items: center;
  box-sizing: border-box;
`;

export const GridCell = styled.div<{align?: "left" | "center" | "right"}>`
  text-align: ${(props) => props.align || "left"};
  box-sizing: border-box;
  overflow: hidden;
  padding: 8px;
`;

export const ErrorMessage = styled(GridCell)`
  color: ${COLORS.TEXT_RED};
  font-size: 11px;
  padding: 0 8px;
  margin-bottom: 4px;
`;

export const LocationTabContainer = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 2px solid #e0e0e0;
  gap: 0;
`;

export const LocationTabButton = styled.button<{isActive: boolean}>`
  background: none;
  border: none;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(props) => (props.isActive ? 600 : 400)};
  color: ${(props) => (props.isActive ? COLORS.BLUE : "#6B7280")};
  border-bottom: 2px solid ${(props) => (props.isActive ? COLORS.BLUE : "transparent")};
  margin-bottom: -2px;
  transition: all 0.2s ease;

  &:hover {
    color: ${COLORS.BLUE};
  }
`;

export const StyledDialog = styled(Dialog)`
  font-family: Roboto, sans-serif;

  .bp5-dialog-body {
    background: #fff;
  }

  .bp5-dialog-header {
    box-shadow: none;
  }

  .bp5-dialog-footer {
    border-top: none;
  }
`;

export const TransferRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 12px;
  align-items: center;
`;

export const DriverLabel = styled.div`
  padding: 5px 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  color: #374151;
`;

// ===== DP-3039 — Swap or Change Driver dialog (Figma 4063-9863) =====

export const DialogSubtitle = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${COLORS.TEXT_GRAY_MEDIUM};
  margin-bottom: 16px;

  em {
    font-style: italic;
  }
`;

/**
 * Segmented Swap/Change toggle: grey track, white active segment (Figma tokens).
 * Blueprint marks the SELECTED segment as a non-minimal button and every INACTIVE segment
 * as `.bp5-minimal` (regardless of intent, which it applies to all). So key the white fill
 * off `:not(.bp5-minimal)` and blend the inactive `.bp5-minimal` segment into the grey track.
 */
export const ModeToggle = styled(SegmentedControl)`
  &.bp5-segmented-control {
    background-color: ${COLORS.BG_GRAY_LIGHT};
    border-radius: 8px;
  }
  margin-bottom: 16px;

  &.bp5-segmented-control > .bp5-button:not(.bp5-minimal) {
    background-color: ${COLORS.WHITE};
    color: #111827;
    box-shadow: 0 1px 2px rgba(4, 5, 34, 0.15);
    border-radius: 6px;
  }

  &.bp5-segmented-control > .bp5-button.bp5-minimal {
    background-color: transparent;
    box-shadow: none;
    color: ${COLORS.TEXT_BLACK};
    border-radius: 6px;
  }
`;

export const FieldLabel = styled.div`
  font-size: 12px;
  line-height: 15px;
  color: ${COLORS.TEXT_GRAY_MEDIUM};
  margin-bottom: 6px;
`;

/** Scope + driver dropdowns side by side (Change mode); single child spans the row (Swap mode). */
export const SelectRow = styled.div<{hasScope: boolean}>`
  display: grid;
  grid-template-columns: ${(props) => (props.hasScope ? "170px 1fr" : "1fr")};
  gap: 8px;
  align-items: center;

  .bp5-html-select {
    width: 100%;
  }

  /* Match the driver picker button + the project's select convention (white, #c3c3c3, r4, h32). */
  .bp5-html-select select {
    height: 32px;
    border-radius: 4px;
    background: ${COLORS.WHITE};
    border: 1px solid #c3c3c3;
    box-shadow: none;
    font-size: 14px;
    color: #111827;
  }
`;

/**
 * DP-3039 driver picker — a Blueprint Select styled to look like the sibling scope field,
 * so each option can show the driver name on the left and the route hint on the right
 * (muted, route letter italic) as native <option> text cannot (Figma 4063-9863).
 */
export const DriverPickerButton = styled(Button)`
  &.bp5-button {
    width: 100%;
    justify-content: space-between;
    background: ${COLORS.WHITE};
    border: 1px solid #c3c3c3;
    border-radius: 4px;
    box-shadow: none;
    height: 32px;
    padding: 0 12px;
    font-size: 14px;
    font-weight: 400;
  }

  &.bp5-button .bp5-button-text {
    flex: 1;
    min-width: 0;
  }
`;

export const DriverPickerValue = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

export const DriverPickerName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #111827;
`;

/** Route hint — muted grey, right-aligned; the route letter itself is italic. */
export const DriverPickerHint = styled.span`
  color: ${COLORS.TEXT_GRAY_MEDIUM};
  white-space: nowrap;
  flex-shrink: 0;

  em {
    font-style: italic;
  }
`;

export const DriverPickerPlaceholder = styled.span`
  color: ${COLORS.COLOR_PLACE_HOLDER};
`;

export const ResultDivider = styled.div`
  border-top: 1px solid ${COLORS.COLOR_BORDER_DIVIDER};
  margin: 16px 0;
`;

/**
 * Result preview row: fixed max-width name column with ellipsis so the "→" arrows
 * stay vertically aligned across rows regardless of name length or the empty state.
 */
export const ResultRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 180px) auto 1fr;
  gap: 12px;
  align-items: center;
  padding: 4px 0;
`;

export const ResultName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ResultTarget = styled.span<{isUnassigned?: boolean}>`
  color: ${(props) => (props.isUnassigned ? COLORS.TEXT_ORANGE : COLORS.TEXT_BLACK)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
